import * as React from 'react';
import {
  Box,
  Icon,
  StackLayout,
  TextAction,
} from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import {
  NumberIcon,
  ArrayActionButtons,
  ComplexObjectArrayControlItem,
  EntityFormControlAnnotationProps,
  SortableItemRenderProps,
} from '@web-config-app/entity-form';
import {
  FlowNode,
  NodeNextKeyMap,
  typeIcons,
} from '@web-config-app/flow-builder';
import type { AnnotatedJsonSchema } from '@web-config-app/core';
import {
  JsonFormsCellRendererRegistryEntry,
  JsonFormsRendererRegistryEntry,
} from '@jsonforms/core';
import { FlowTransitions } from './flow-transitions.component';
import { FlowNodeArrayItemDrawer } from './flow-node-array-item-drawer.component';

export interface FlowNodeArrayItemProps {
  itemLabel: string;
  defaultLabel?: string;
  itemNumber: number;
  itemData: FlowNode;
  path: string;
  handleOnMoveDown: (path: string, index: number) => () => void;
  handleOnMoveUp: (path: string, index: number) => () => void;
  handleRemoveItem: (path: string, index: number) => () => void;
  handleDuplicateItem: (itemData: any, index: number) => () => void;
  onMoveToPosition: (
    itemPath: string,
    fromIndex: number,
  ) => (toIndex: number) => void;
  index: number;
  arrayLength: number;
  disableActions: boolean;
  data: ComplexObjectArrayControlItem[];
  nodeNextKeyMap: NodeNextKeyMap;
  schema: AnnotatedJsonSchema;
  rootSchema: AnnotatedJsonSchema;
  enabled: boolean;
  renderers: JsonFormsRendererRegistryEntry[] | undefined;
  cells: JsonFormsCellRendererRegistryEntry[] | undefined;
  combinatorProperties?: EntityFormControlAnnotationProps['combinatorProperties'];
  onModalStateChange?: (activeArrayItemIndex: number, isOpen: boolean) => void;
  activeArrayItemIndex?: number | null;
  hasError?: boolean;
  dragHandleProps?: SortableItemRenderProps['dragHandleProps'];
}

export const FlowNodeArrayItem: React.FC<FlowNodeArrayItemProps> = ({
  itemLabel,
  itemNumber,
  itemData,
  path,
  handleOnMoveDown,
  handleOnMoveUp,
  handleRemoveItem,
  handleDuplicateItem,
  onMoveToPosition,
  index,
  arrayLength,
  disableActions,
  data,
  nodeNextKeyMap,
  schema,
  rootSchema,
  enabled,
  renderers,
  cells,
  combinatorProperties,
  onModalStateChange,
  activeArrayItemIndex,
  defaultLabel,
  hasError = false,
  dragHandleProps,
}) => {
  const isDrawerOpen = activeArrayItemIndex === index;
  const { formatMessage } = useIntl();

  const handleOpenChange = React.useCallback(
    (open: boolean) => {
      onModalStateChange?.(index, open);
    },
    [index, onModalStateChange],
  );

  const itemNodeType = itemData?.nodeType;
  const itemNodeTypeIcon =
    itemNodeType && itemNodeType in typeIcons
      ? typeIcons[itemNodeType as keyof typeof typeIcons]
      : undefined;

  return (
    <Box css={{ width: '100%' }}>
      <StackLayout
        data-testid="flow-node-array-item-container"
        orientation="horizontal"
        spacing="$one"
        horizontalAlignment="stretch"
        verticalAlignment="center"
      >
        <StackLayout
          orientation="vertical"
          verticalAlignment="center"
          spacing="$threeQuarters"
          horizontalAlignment="stretch"
        >
          <StackLayout
            orientation="horizontal"
            spacing="$one"
            horizontalAlignment="stretch"
            verticalAlignment="center"
          >
            <Box css={{ flexGrow: 0, flexShrink: 0 }}>
              <NumberIcon number={itemNumber} />
            </Box>
            <Box css={{ flexGrow: 1 }}>
              <StackLayout orientation="horizontal" spacing="$quarter">
                <Icon
                  icon={itemNodeTypeIcon}
                  size="$one"
                  tint="$onSurfaceIconPrimary"
                  css={{ marginBlockStart: '5px' }}
                />
                <FlowNodeArrayItemDrawer
                  itemLabel={itemLabel}
                  schema={schema}
                  rootSchema={rootSchema}
                  path={path}
                  index={index}
                  enabled={enabled}
                  renderers={renderers}
                  cells={cells}
                  combinatorProperties={combinatorProperties}
                  data={data}
                  isOpen={isDrawerOpen}
                  onOpenChange={handleOpenChange}
                  activeArrayItemIndex={activeArrayItemIndex}
                />
              </StackLayout>
            </Box>
          </StackLayout>
          <FlowTransitions
            itemData={itemData}
            data={data}
            nodeNextKeyMap={nodeNextKeyMap}
          />
        </StackLayout>
        <Box css={{ flexGrow: 0, flexShrink: 0 }}>
          <ArrayActionButtons
            showDeleteModal
            onDuplicate={handleDuplicateItem(itemData, index)}
            onDelete={handleRemoveItem(path, index)}
            onMoveDown={handleOnMoveDown(path, index)}
            onMoveUp={handleOnMoveUp(path, index)}
            onMoveToPosition={onMoveToPosition(path, index)}
            itemLabel={itemLabel as string}
            itemIndex={index}
            arrayLength={arrayLength}
            disableAllActions={disableActions}
            dragHandleProps={dragHandleProps}
          />
        </Box>
      </StackLayout>
      {hasError ? (
        <TextAction
          css={{ marginBlockStart: '$half', typography: '$bodyTwo' }}
          destructive
          as="button"
          onClick={() => handleOpenChange(true)}
        >
          <Icon
            icon="statusErrorStatusFilled"
            size="$one"
            tint="$criticalIcon"
            css={{ marginInlineEnd: '$quarter' }}
          />
          {formatMessage(
            { id: 'THERE_ARE_ERRORS_IN_THIS' },
            { item: defaultLabel ?? 'item' },
          )}
          <Icon
            icon="interfaceArrowRight"
            size="$threeQuarters"
            tint="$criticalIcon"
            css={{ marginInlineStart: '$half' }}
          />
        </TextAction>
      ) : null}
    </Box>
  );
};
